import React, { useState, useEffect } from 'react';
import '../../styles/users-responsive.css';
import styled from 'styled-components';
import { FaUser, FaTrash, FaEdit, FaPlus, FaEnvelope, FaUserTag, FaSync } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import Button from '../common/Button';
import { User } from '../../types/user';
import { usersService } from '../../services/supabase/users.service';
import { useNotifications } from '../../context/NotificationContext';
import { enhancedTextContrast, enhancedHeadingContrast } from '../../styles/contrastUtils';

const UsersListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  padding: 0;
  box-sizing: border-box;
`;
// eslint-disable-next-line unused-imports/no-unused-vars
const AddUserButton = styled(Button)`
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 0.85rem;
  }
  
  @media (max-width: 480px) {
    padding: 6px 10px;
    font-size: 0.8rem;
  }
`;

const UsersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  /* Adaptation pour mobile */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const UsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
  width: 100%;
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
  
  /* Style spécifique pour Samsung Galaxy et autres appareils mobiles */
  @media (max-width: 480px) {
    background-color: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.1);
    padding: 0.5rem;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 480px) {
    flex-direction: row;
    align-items: center;
    width: 100%;
    gap: 0.5rem;
  }
`;

const UserAvatar = styled.div<{ $imageUrl?: string | null }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme, $imageUrl }) => $imageUrl ? 'transparent' : theme.colors.primary};
  background-image: ${({ $imageUrl }) => $imageUrl ? `url(${$imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (max-width: 480px) {
    align-items: flex-start;
    text-align: left;
    width: calc(100% - 40px);
    margin-left: 0.25rem;
  }
`;

const UserName = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: #000000;
  
  /* Style spécifique pour Samsung Galaxy et autres appareils mobiles */
  @media (max-width: 480px) {
    ${enhancedHeadingContrast}
    font-weight: 700;
  }
`;

const UserEmail = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textLight};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  /* Style spécifique pour Samsung Galaxy et autres appareils mobiles */
  @media (max-width: 480px) {
    ${enhancedTextContrast}
  }
`;

const UserRole = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textLight};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  /* Style spécifique pour Samsung Galaxy et autres appareils mobiles */
  @media (max-width: 480px) {
    ${enhancedTextContrast}
  }
`;

const UserActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  
  @media (max-width: 480px) {
    margin-top: 0.25rem;
    width: 100%;
    justify-content: flex-end;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${({ theme }) => theme.colors.border};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
  
  &.delete {
    color: ${({ theme }) => theme.colors.danger};
    &:hover {
      background-color: ${({ theme }) => theme.colors.dangerLight};
    }
  }
  
  &.edit {
    color: ${({ theme }) => theme.colors.primary};
    &:hover {
      background-color: ${({ theme }) => theme.colors.primaryLight};
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
  gap: 1rem;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const OwnerBadge = styled.span`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  margin-left: 0.5rem;
`;

interface UsersListComponentProps {
  onAddUser: () => void;
  onEditUser: (user: User) => void;
}

const UsersListComponent: React.FC<UsersListComponentProps> = ({ onAddUser, onEditUser }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const { supabaseUser } = useAuth();
  const { addNotification } = useNotifications();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await usersService.getUsersByAgency();
      
      if (error) {
        throw error;
      }
      
      setUsers(data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
      addNotification({
        type: 'info',
        title: 'Information',
        message: 'Impossible de récupérer la liste des utilisateurs',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      return;
    }
    
    try {
      const { error } = await usersService.deleteUser(userId);
      
      if (error) {
        throw error;
      }
      
      // Rafraîchir la liste des utilisateurs
      fetchUsers();
      
      addNotification({
        type: 'info',
        title: 'Succès',
        message: 'Utilisateur supprimé avec succès',
      });
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', error);
      addNotification({
        type: 'warning',
        title: 'Erreur',
        message: 'Impossible de supprimer l\'utilisateur',
      });
    }
  };

  // Extraire l'initiale du nom d'utilisateur pour l'avatar
  const getUserInitial = (user: User) => {
    return user.first_name && user.name
      ? `${user.first_name[0]}${user.name[0]}`
      : user.email
        ? user.email[0].toUpperCase()
        : 'U';
  };

  if (loading) {
    return (
      <LoadingState>
        Chargement des utilisateurs...
      </LoadingState>
    );
  }

  return (
    <UsersListContainer className="users-list-container">
      <UsersHeader className="users-header">
        <h2>Utilisateurs de l'agence</h2>
        <div style={{ display: 'flex', gap: '0.5rem', width: '100%', justifyContent: 'flex-end' }}>
          <Button 
            onClick={fetchUsers} 
            $variant="secondary" 
            style={{ padding: '0.5rem', minWidth: 'auto' }}
            title="Rafraîchir la liste"
          >
            <FaSync size={14} />
          </Button>
          <Button onClick={onAddUser} $variant="primary" className="add-user-button">
            <FaPlus size={14} style={{ marginRight: '8px' }} />
            Ajouter un utilisateur
          </Button>
        </div>
      </UsersHeader>
      
      {users.length === 0 ? (
        <EmptyState>
          <FaUser size={48} color="#ccc" />
          <h3>Aucun utilisateur</h3>
          <p>Vous n'avez pas encore d'utilisateurs dans votre agence.</p>
          <Button onClick={onAddUser} $variant="primary" className="add-user-button">
            <FaPlus size={14} style={{ marginRight: '8px' }} />
            Ajouter un utilisateur
          </Button>
        </EmptyState>
      ) : (
        <UsersList>
          {users.map(user => (
            <UserCard key={user.id} className="user-card">
              <UserInfo className="user-info">
                <UserAvatar $imageUrl={user.photo_url} className="user-avatar">
                  {!user.photo_url && getUserInitial(user)}
                </UserAvatar>
                <UserDetails>
                  <UserName className="user-name">
                    {user.first_name} {user.name}
                    {user.is_owner && <OwnerBadge className="badge">Propriétaire</OwnerBadge>}
                  </UserName>
                  <UserEmail className="user-email">
                    <FaEnvelope size={10} style={{ marginRight: '2px' }} />
                    {user.email}
                  </UserEmail>
                  {user.role && (
                    <UserRole className="user-role">
                      <FaUserTag size={10} style={{ marginRight: '2px' }} />
                      {user.role}
                    </UserRole>
                  )}
                </UserDetails>
              </UserInfo>
              <UserActions>
                <ActionButton 
                  className="edit action-button"
                  onClick={() => onEditUser(user)}
                  aria-label="Modifier l'utilisateur"
                >
                  <FaEdit size={14} />
                </ActionButton>
                {!user.is_owner && supabaseUser?.id !== user.id && (
                  <ActionButton 
                    className="delete action-button"
                    onClick={() => handleDeleteUser(user.id)}
                    aria-label="Supprimer l'utilisateur"
                  >
                    <FaTrash size={14} />
                  </ActionButton>
                )}
              </UserActions>
            </UserCard>
          ))}
        </UsersList>
      )}
    </UsersListContainer>
  );
};

export default UsersListComponent;

